import {
    getPerPageOptions,
    onTooltipShow, getDate
} from '@shared/components/admin/pages/Management/common-data';

// booleanSelectKeys: 기본값이 null 이고 선택지 값이 true/false 인 select 필드. 쿼리의 'true'/'false' 를 boolean 으로 복원한다.
// 쿼리는 문자열이라 비교는 문자열 기준으로 한다.
const normalize = v => v == null ? null : String(v);
// 1 이상 정수 문자열만 페이지로 인정, 아니면 1
const parsePage = v => {
    const n = typeof v === 'string' ? Number(v) : NaN;
    return Number.isInteger(n) && n >= 1 ? n : 1;
};
const isEmpty = v => v == null || v === '';

export default function PaginationPageMixin({searchForm = {}, pagination = {}, booleanSelectKeys = []}) {
    // 기본값은 URL 에서 키를 생략한다. data() 복제 전에 고정해 둔다.
    const defaultPerPage = pagination?.perPage ?? 20;
    const defaultQuery = {current_page: 1, per_page: defaultPerPage};
    // 빈 값(null·''·undefined)과 기본값을 같은 값으로 보기 위한 비교용 문자열. boolean 기본값 필드는 true 만 값이고 false·'false' 는 빈 값(복원 규칙과 동일).
    const effective = (key, v) => normalize(
        (typeof searchForm[key] === 'boolean' && v !== true && v !== 'true') || isEmpty(v) ? (defaultQuery[key] ?? null) : v
    );
    return {
        data() {
            return {
                listLoading: false,
                searchResults: null,
                searchFormValue: JSON.parse(JSON.stringify(searchForm)),
                pagination: pagination && {...pagination},
                perPageOptions: getPerPageOptions()
            };
        },
        computed: {
            get_tooltip_title() {
                return (data) => {
                    return data ? {title: data} : null;
                };
            },
            get_date() {
                return (date) => {
                    return getDate(date);
                };
            }
        },
        watch: {
            // b-pagination 은 totalRows 0 으로 만들어지면 내부 페이지를 1로 깎는다(목록 → 상세 → 뒤로가기, 새로고침).
            // 화면 값이 이미 같은 페이지면 v-model 이 바뀌지 않아 내부 값이 1로 남으므로, 총 건수가 들어오면 비웠다가 다시 넣는다
            // (ManageCondition 등과 같은 방식). setSearchDataFormQuery 로 URL 을 읽는 화면만(MailSnippet 등 자체 페이징은 그대로).
            'pagination.totalRows'() {
                if (this.pagination == null || !this.usesRoutePaging) return;
                const page = parsePage(this.$route.query.current_page);
                if (page <= 1) return;
                this.pagination.currentPage = null;
                setTimeout(() => { this.pagination.currentPage = page; });
            }
        },
        mounted() {
            this.$root.$on('bv::tooltip::show', this.onTooltipShow);
        },
        beforeDestroy() {
            this.$root.$off('bv::tooltip::show', this.onTooltipShow);
        },
        methods: {
            onTooltipShow,
            setSearchDataFormQuery() {
                Object.keys(this.searchFormValue).forEach(key => {
                    const value = this.$route.query[key];
                    if (typeof searchForm[key] === 'boolean') {
                        // 쿼리는 문자열이라 boolean 필드는 'true' 만 true 로 복원 (false 는 쿼리에 싣지 않음)
                        this.searchFormValue[key] = value === 'true';
                    } else if (value !== undefined && value !== '') {
                        this.searchFormValue[key] = booleanSelectKeys.includes(key) && (value === 'true' || value === 'false')
                            ? value === 'true' : value;
                    } else {
                        this.searchFormValue[key] = null;
                    }
                });
                
                if (this.pagination != null) {
                    this.usesRoutePaging = true; // 비반응 플래그
                    // 1 이상 정수 / perPageOptions 안의 값만 받고 아니면 기본값 (배열 등 비정상 쿼리 포함)
                    const {current_page: page, per_page: perPage} = this.$route.query;
                    const perPageNum = typeof perPage === 'string' ? Number(perPage) : NaN;
                    this.pagination.currentPage = parsePage(page);
                    this.pagination.perPage = this.perPageOptions.some(o => o.value === perPageNum) ? perPageNum : defaultPerPage;
                }
            },
            async changeQuery({page, pageSize}) {
                const query = {
                    ...this.$route.query,
                };
                let checkSameQuery = true;
                
                
                // 페이지 이동(page/pageSize 지정)은 아직 제출하지 않은 검색 폼 값을 섞지 않고 URL 에 적용된 값만 유지.
                // 검색 제출(changeQuery({}))일 때만 폼 값을 URL 에 반영한다.
                const isPaging = page !== undefined || pageSize !== undefined;
                const toQueryObj = isPaging ? {} : {
                    ...this.searchFormValue,
                };
                if (this.pagination != null) {
                    // 페이지 크기를 바꾸면 1페이지로
                    toQueryObj.current_page = page ?? (pageSize !== undefined ? 1 : this.pagination.currentPage);
                    toQueryObj.per_page = pageSize ?? this.pagination.perPage;
                }
                
                Object.entries(toQueryObj).forEach(([key, item]) => {
                    // 빈 값·기본값은 같은 조건으로 비교한다. 기본값이 boolean 인 필드(체크박스)의 false 는 빈 값이고
                    // ('false' 문자열이 truthy 로 읽히는 것 방지), 기본값이 null 인 선택 필드의 false(활성 상태: 비활성)는 필터 값이다.
                    if (effective(key, query[key]) !== effective(key, item)) {
                        checkSameQuery = false;
                        query[key] = item;
                    }
                });

                if (!checkSameQuery) {
                    // 이동할 때 이 mixin 의 키 중 빈 값·기본값은 URL 에서 뺀다. tab 등 다른 키는 건드리지 않는다.
                    const ownKeys = Object.keys(searchForm);
                    if (this.pagination != null) ownKeys.push('current_page', 'per_page');
                    ownKeys.forEach(key => {
                        if (key in query && effective(key, query[key]) === effective(key, undefined)) delete query[key];
                    });
                }
                
                if (!checkSameQuery) {
                    await this.$router.push({query}).catch(e => {
                    });
                }
                return checkSameQuery;
            },
            resetForm() {
                this.searchFormValue = JSON.parse(JSON.stringify(searchForm));
                if (this.pagination != null) {
                    this.pagination.currentPage = 1;
                }
            },
            // 초기화 버튼: 폼을 비우고 URL 에서 필터를 지운 뒤 다시 조회한다 (URL 이 이미 같으면 watcher 가 안 돌므로 직접 조회).
            async resetSearch() {
                this.resetForm();
                if (await this.changeQuery({})) await this.fetchData();
            },
        },
    };
}
